/**
 * Shared helpers for GA4 event / ticket context.
 * event_id and ticket_id must be platform UUIDs — never slugs — so admin &
 * organizer dashboards can filter GA4 custom dimensions reliably.
 */

// Versions 1-8 per RFC 9562. Platform IDs are UUIDv7 (time-ordered, e.g.
// 01a07a9c-fcba-71d3-…); the previous [1-5] rejected every one of them, so
// resolveEventId() returned undefined for all events — Meta's ViewContent
// was never sent and GA4 events went out without their event_id dimension.
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(value) {
  if (value == null || value === "") return false;
  return UUID_REGEX.test(String(value).trim());
}

export function resolveEventId(...candidates) {
  for (const candidate of candidates) {
    if (isUuid(candidate)) return String(candidate).trim();
  }
  return undefined;
}

export function resolveTicketId(...candidates) {
  for (const candidate of candidates) {
    if (isUuid(candidate)) return String(candidate).trim();
  }
  return undefined;
}

/**
 * Merge normalized event context onto GA4 params.
 * Omits event_id when no UUID is available (avoids "(not set)" noise from slugs).
 */
export function withEventContext(params = {}, context = {}) {
  const eventId = resolveEventId(
    context.event_id,
    context.eventId,
    context.id,
  );
  const eventName =
    context.event_name ||
    context.eventName ||
    context.name ||
    undefined;

  const next = { ...params };

  if (eventId) next.event_id = eventId;
  if (eventName) next.event_name = String(eventName);

  if (context.event_city || context.city) {
    next.event_city = context.event_city || context.city;
  }
  if (context.event_date || context.start_date) {
    next.event_date = context.event_date || context.start_date;
  }
  if (context.event_category || context.category) {
    next.event_category = context.event_category || context.category;
  }
  if (context.event_venue || context.venue) {
    const v = context.event_venue || context.venue;
    next.event_venue =
      typeof v === "string" ? v : v?.name || v?.venue_name || undefined;
  }

  return next;
}

/**
 * Merge normalized ticket context onto GA4 params.
 */
export function withTicketContext(params = {}, context = {}) {
  const ticketId = resolveTicketId(
    context.ticket_id,
    context.ticketId,
    context.id,
  );
  const ticketName =
    context.ticket_name || context.ticketName || context.name || undefined;

  const next = { ...params };

  if (ticketId) next.ticket_id = ticketId;
  if (ticketName) next.ticket_name = String(ticketName);
  if (context.ticket_type) next.ticket_type = context.ticket_type;

  return next;
}

/**
 * Build a GA4 items[] row for tickets (item_id = ticket UUID when available).
 */
export function buildTicketItem({
  ticket_id,
  ticketId,
  ticket_name,
  ticketName,
  event_id,
  eventId,
  price = 0,
  quantity = 1,
  ticket_type,
}) {
  const tId = resolveTicketId(ticket_id, ticketId);
  const eId = resolveEventId(event_id, eventId);
  const name = ticket_name || ticketName || "";
  const qty = Number(quantity) || 1;
  const unitPrice = Number(price) || 0;

  return {
    item_id: tId || eId || "",
    item_name: name,
    item_variant: ticket_type || undefined,
    price: unitPrice,
    quantity: qty,
  };
}

/**
 * Build a GA4 items[] row for events (item_id = event UUID when available).
 */
export function buildEventItem({
  event_id,
  eventId,
  id,
  slug,
  event_name,
  eventName,
  name,
  price = 0,
  category,
  city,
}) {
  const eId = resolveEventId(event_id, eventId, id);

  return {
    item_id: eId || (slug ? String(slug) : ""),
    item_name: event_name || eventName || name || "",
    item_category: category || undefined,
    price: Number(price) || 0,
    location_id: city || undefined,
  };
}
